/**
 * Theme Configuration & Utilities
 *
 * FRAMEWORK-AGNOSTIC: This file contains no React imports.
 * Can be used in any TypeScript environment.
 */

/**
 * Supported theme modes
 */
export type ThemeMode = 'light' | 'dark' | 'system'

/**
 * Resolved theme (actual theme applied, after system preference)
 */
export type ResolvedTheme = 'light' | 'dark'

/**
 * Theme configuration for Slate monochromatic design system
 */
export interface ThemeConfig {
  colors: {
    // Primary (Brand Core) - Slate 900
    primary: string
    primaryForeground: string

    // Secondary (Accents/CTA) - Slate 100
    secondary: string
    secondaryForeground: string

    // Background (Surface)
    background: string
    foreground: string

    // Card
    card: string
    cardForeground: string

    // Muted
    muted: string
    mutedForeground: string

    // Accent
    accent: string
    accentForeground: string

    // Destructive (Error)
    destructive: string
    destructiveForeground: string

    // Border
    border: string
    input: string
    ring: string
  }
  radius: string
}

/**
 * Light theme configuration (Slate monochromatic)
 */
export const lightTheme: ThemeConfig = {
  colors: {
    primary: '222.2 47.4% 11.2%', // Slate 900
    primaryForeground: '210 40% 98%',

    secondary: '210 40% 96.1%', // Slate 100
    secondaryForeground: '222.2 47.4% 11.2%',

    background: '0 0% 100%', // White
    foreground: '222.2 84% 4.9%', // Slate 950

    card: '0 0% 100%', // White
    cardForeground: '222.2 84% 4.9%',

    muted: '210 40% 96.1%', // Slate 100
    mutedForeground: '215.4 16.3% 46.9%', // Slate 500

    accent: '210 40% 96.1%', // Slate 100
    accentForeground: '222.2 47.4% 11.2%',

    destructive: '0 84.2% 60.2%', // Red 500
    destructiveForeground: '210 40% 98%',

    border: '214.3 31.8% 91.4%', // Slate 200
    input: '214.3 31.8% 91.4%',
    ring: '222.2 84% 4.9%', // Slate 950
  },
  radius: '0.5rem',
}

/**
 * Dark theme configuration (Slate monochromatic)
 */
export const darkTheme: ThemeConfig = {
  colors: {
    primary: '210 40% 98%', // Slate 50
    primaryForeground: '222.2 47.4% 11.2%',

    secondary: '217.2 32.6% 17.5%', // Slate 800
    secondaryForeground: '210 40% 98%',

    background: '222.2 84% 4.9%', // Slate 950
    foreground: '210 40% 98%', // Slate 50

    card: '222.2 84% 4.9%', // Slate 950
    cardForeground: '210 40% 98%',

    muted: '217.2 32.6% 17.5%', // Slate 800
    mutedForeground: '215 20.2% 65.1%', // Slate 400

    accent: '217.2 32.6% 17.5%', // Slate 800
    accentForeground: '210 40% 98%',

    destructive: '0 62.8% 30.6%', // Red 900
    destructiveForeground: '210 40% 98%',

    border: '217.2 32.6% 17.5%', // Slate 800
    input: '217.2 32.6% 17.5%',
    ring: '212.7 26.8% 83.9%', // Slate 300
  },
  radius: '0.5rem',
}

/**
 * Get theme configuration by mode
 */
export function getThemeConfig(mode: ResolvedTheme): ThemeConfig {
  return mode === 'dark' ? darkTheme : lightTheme
}

/**
 * Get system theme preference
 * @returns 'dark' if system prefers dark mode, 'light' otherwise
 */
export function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light'

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Resolve theme mode to actual theme
 * @param mode Theme mode (light, dark, or system)
 * @returns Resolved theme (light or dark)
 */
export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') {
    return getSystemTheme()
  }
  return mode
}

/**
 * Apply theme to document root
 * This is the SINGLE SOURCE OF TRUTH for theme application
 *
 * @param theme Theme to apply ('light' or 'dark')
 */
export function applyTheme(theme: ResolvedTheme): void {
  if (typeof window === 'undefined') return

  const root = document.documentElement

  // Remove existing theme class
  root.classList.remove('light', 'dark')

  // Add new theme class
  root.classList.add(theme)

  // Store theme preference in localStorage
  localStorage.setItem('theme', theme)
}

/**
 * Get stored theme preference
 * @returns Stored theme mode, or 'system' if not set
 */
export function getStoredTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'system'

  const stored = localStorage.getItem('theme')
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored
  }

  return 'system'
}

/**
 * Store theme preference
 * @param mode Theme mode to store
 */
export function storeTheme(mode: ThemeMode): void {
  if (typeof window === 'undefined') return

  localStorage.setItem('theme', mode)
}

/**
 * Initialize theme on app load
 * Should be called once on application startup
 *
 * @returns The resolved theme that was applied
 */
export function initializeTheme(): ResolvedTheme {
  const storedMode = getStoredTheme()
  const resolvedTheme = resolveTheme(storedMode)
  applyTheme(resolvedTheme)
  return resolvedTheme
}
